import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Box, Popover } from "@mui/material";
import { FiUser } from "react-icons/fi";
import AssigneeHoverCard from "./AssigneeHoverCard";
import { normalizeAssignees, initials, hueFor, completionSummary, isSoloView } from "./utils";
import { STATUS_LABEL } from "../../pages/TodoTask/constants/todoTokens";
import { getCurrentUser } from "../../utils/currentUser";
import { updateAssigneeStatus } from "../../services/todoTask.service";
import { showErrorToast } from "../../utils/Toaster/AppToast";
import { T } from "../../pages/TodoTask/constants/todoTokens";

const OPEN_DELAY = 120;  // stops the card flashing while the cursor crosses a column
const CLOSE_DELAY = 180; // travel budget from the anchor into the card

const PLACEMENTS = {
  top:    { anchorOrigin: { vertical: "top",    horizontal: "center" }, transformOrigin: { vertical: "bottom", horizontal: "center" } },
  bottom: { anchorOrigin: { vertical: "bottom", horizontal: "center" }, transformOrigin: { vertical: "top",    horizontal: "center" } },
  right:  { anchorOrigin: { vertical: "center", horizontal: "right"  }, transformOrigin: { vertical: "center", horizontal: "left"   } },
  left:   { anchorOrigin: { vertical: "center", horizontal: "left"   }, transformOrigin: { vertical: "center", horizontal: "right"  } },
};

/**
 * AssigneeGroup — overlapping avatar stack + interactive per-assignee card.
 *
 * task           – the task doc (reads `assignees`, falls back to legacy scalars)
 * assignees      – optional pre-normalised override (skips `task`)
 * taskId         – required to write; defaults to task._id
 * assignedBy     – task creator id; defaults to task.assignedBy
 * size           – avatar px (18 | 24 | 26). Ring/overlap/font scale from it.
 * max            – avatars before the +N chip (default 3)
 * readOnly       – render the card but never allow status edits
 * disabled       – suppress the card entirely (e.g. mid-drag)
 * placement      – "top" | "bottom" | "left" | "right"
 * currentUserId / isAdmin – overrides (TaskDetails supplies a JWT-derived id)
 * onChanged      – (taskId, assigneeId, newStatus, res) => void after a write
 */
const AssigneeGroup = ({
  task,
  assignees: assigneesProp,
  taskId,
  assignedBy,
  size = 24,
  max = 3,
  readOnly = false,
  disabled = false,
  placement = "top",
  currentUserId,
  isAdmin,
  onChanged,
}) => {
  // ── permissions (hoisted above `base` so `solo` can filter the roster) ─────
  const me = useMemo(() => getCurrentUser(), []);
  const uid = String(currentUserId ?? me.userId ?? "");
  const admin = isAdmin ?? me.isAdmin;
  const creatorId = String(assignedBy ?? task?.assignedBy?._id ?? task?.assignedBy ?? "");
  const isCreator = Boolean(uid && creatorId && uid === creatorId);

  const solo = useMemo(
    () => isSoloView(task, { currentUserId: uid, isAdmin: admin }),
    [task, uid, admin],
  );

  const base = useMemo(() => {
    const list = assigneesProp ? assigneesProp : normalizeAssignees(task);
    // Fails closed: on a solo view render only the viewer's own line, never
    // someone else's, even if a stale response still carries the full roster.
    if (!solo) return list;
    return list.filter((a) => String(a._id) === uid);
  }, [assigneesProp, task, solo, uid]);

  // Optimistic overrides: id -> status. Cleared when server data catches up, so
  // there is exactly one source of truth after reconciliation.
  const [overrides, setOverrides] = useState({});
  const [pendingId, setPendingId] = useState(null);
  useEffect(() => { setOverrides({}); }, [base]);

  const assignees = useMemo(
    () => base.map((a) => (overrides[a._id] ? { ...a, status: overrides[a._id] } : a)),
    [base, overrides],
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [pinned, setPinned] = useState(false); // click / tap / keyboard — sticky
  const menuOpenRef = useRef(false);           // don't close while a status menu is open
  const openT = useRef(null);
  const closeT = useRef(null);
  const anchorRef = useRef(null);
  const open = Boolean(anchorEl);

  const clearTimers = () => { clearTimeout(openT.current); clearTimeout(closeT.current); };
  useEffect(() => () => clearTimers(), []);

  const scheduleOpen = useCallback(() => {
    if (disabled || !assignees.length) return;
    clearTimers();
    openT.current = setTimeout(() => setAnchorEl(anchorRef.current), OPEN_DELAY);
  }, [disabled, assignees.length]);

  const scheduleClose = useCallback(() => {
    if (pinned) return;
    clearTimers();
    closeT.current = setTimeout(() => {
      if (!menuOpenRef.current) setAnchorEl(null);
    }, CLOSE_DELAY);
  }, [pinned]);

  const openNow = useCallback(() => {
    if (disabled || !assignees.length) return;
    clearTimers();
    setAnchorEl(anchorRef.current);
  }, [disabled, assignees.length]);

  const closeNow = useCallback(() => {
    clearTimers();
    setPinned(false);
    setAnchorEl(null);
  }, []);

  const canEdit = useCallback(
    (a) =>
      !readOnly &&
      Boolean(taskId ?? task?._id) &&
      (admin || isCreator || String(a._id) === uid),
    [readOnly, taskId, task, admin, isCreator, uid],
  );

  // ── optimistic write ───────────────────────────────────────────────────────
  const handlePickStatus = useCallback(
    async (assigneeId, nextStatus) => {
      const id = taskId ?? task?._id;
      if (!id) return;
      const prev = base.find((a) => a._id === assigneeId)?.status;
      setOverrides((o) => ({ ...o, [assigneeId]: nextStatus }));
      setPendingId(assigneeId);
      try {
        const res = await updateAssigneeStatus(id, assigneeId, nextStatus);
        if (res?.error === false) {
          onChanged?.(id, assigneeId, nextStatus, res);
        } else {
          setOverrides((o) => ({ ...o, [assigneeId]: prev }));
          showErrorToast(res?.message || "Could not update status");
        }
      } catch (err) {
        setOverrides((o) => ({ ...o, [assigneeId]: prev }));
        showErrorToast(err?.data?.message || err?.message || "Could not update status");
      } finally {
        setPendingId(null);
      }
    },
    [taskId, task, base, onChanged],
  );

  // ── geometry ───────────────────────────────────────────────────────────────
  const ring = size >= 24 ? 2 : 1.5;
  const overlap = -Math.round(size * 0.32);
  const font = size >= 26 ? 10 : size >= 24 ? 9 : 8;
  const shown = assignees.slice(0, max);
  const extra = assignees.length - shown.length;
  const { done, total } = completionSummary(assignees);

  if (!assignees.length) {
    return (
      <Avatar
        aria-label="Unassigned"
        sx={{
          width: size, height: size, fontSize: `${font}px`, fontWeight: 700,
          bgcolor: "#E2E8F0", color: "#94A3B8",
          border: `${ring}px solid #fff`, boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
        }}
      >
        <FiUser size={Math.round(size * 0.42)} />
      </Avatar>
    );
  }

  // The old label announced the group size to screen readers.
  const label = solo
    ? `Assigned to you — ${STATUS_LABEL[assignees[0]?.status] || "To Do"}`
    : `${total} assignee${total > 1 ? "s" : ""}, ${done} of ${total} done`;
  const place = PLACEMENTS[placement] || PLACEMENTS.top;

  return (
    <>
      <Box
        ref={anchorRef}
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        // Never start a react-beautiful-dnd drag from the stack.
        onMouseDown={(e) => e.stopPropagation()}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onFocus={openNow}
        onBlur={(e) => {
          if (e.currentTarget.contains(e.relatedTarget)) return;
          if (!pinned) closeNow();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            setPinned((p) => !p);
            if (open) closeNow(); else openNow();
          }
          if (e.key === "Escape" && open) {
            e.stopPropagation();
            closeNow();
            anchorRef.current?.focus();
          }
        }}
        // Don't let the card's own onEditTask fire.
        onClick={(e) => { e.stopPropagation(); setPinned(true); openNow(); }}
        // Touch has no hover, so a tap pins the card open.
        onTouchStart={(e) => { e.stopPropagation(); setPinned(true); openNow(); }}
        sx={{
          display: "inline-flex", alignItems: "center", cursor: "pointer",
          borderRadius: 999, p: "1px",
          "&:focus-visible": { outline: "none", boxShadow: `0 0 0 3px ${T.primaryGlow}` },
          "& > *:not(:first-of-type)": { ml: `${overlap}px` },
          "&:hover > *": { transform: "translateY(-1px)" },
        }}
      >
        {shown.map((a) => (
          <Avatar
            key={a.lineId || a._id}
            src={a.avatar || undefined}
            sx={{
              width: size, height: size, fontSize: `${font}px`, fontWeight: 700,
              bgcolor: hueFor(a._id), color: "#fff",
              border: `${ring}px solid #fff`,
              boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
              transition: "transform .16s cubic-bezier(.16,1,.3,1)",
            }}
          >
            {initials(a.name)}
          </Avatar>
        ))}
        {extra > 0 && (
          <Avatar
            sx={{
              width: size, height: size, fontSize: `${font}px`, fontWeight: 800,
              bgcolor: "#F1F5F9", color: T.textSub,
              border: `${ring}px solid #fff`,
              boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
              transition: "transform .16s cubic-bezier(.16,1,.3,1)",
            }}
          >
            +{extra}
          </Avatar>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closeNow}
        disableRestoreFocus
        disableAutoFocus={!pinned}
        disableEnforceFocus={!pinned}
        hideBackdrop={!pinned}
        {...place}
        // The root is transparent to the mouse so the anchor's mouseleave still
        // fires, while the paper stays interactive so the cursor can travel into
        // it. Same technique as ReportCategory/TicketChannelTable.
        sx={{
          ...(pinned ? {} : { pointerEvents: "none" }),
          "& .MuiPopover-paper": { pointerEvents: "auto" },
        }}
        onMouseEnter={clearTimers}
        onMouseLeave={scheduleClose}
        slotProps={{
          paper: {
            role: "dialog",
            "aria-label": label,
            elevation: 0,
            sx: {
              mt: placement === "bottom" ? 1 : 0,
              mb: placement === "top" ? 1 : 0,
              ml: placement === "right" ? 1 : 0,
              mr: placement === "left" ? 1 : 0,
              bgcolor: "transparent", overflow: "visible", borderRadius: "12px",
            },
          },
        }}
        transitionDuration={{ enter: 140, exit: 90 }}
      >
        <AssigneeHoverCard
          assignees={assignees}
          canEdit={canEdit}
          pendingId={pendingId}
          onPickStatus={handlePickStatus}
          onMenuOpenChange={(v) => { menuOpenRef.current = v; if (v) clearTimers(); }}
          solo={solo}
        />
      </Popover>
    </>
  );
};

export default React.memo(AssigneeGroup);
